/**
 * Guided-walkthrough store.
 *
 * A guided walkthrough is a short, DETERMINISTIC, LLM-free sequence of pointing
 * steps. The Companion's LLM decides *whether* to start one (its intent-driven
 * `get_platform_guide` tool call), but once an authored `steps` array is
 * retrieved the flow runs with zero further model involvement — the steps live
 * in the knowledge-base row (`ai_knowledge_base.metadata.steps`), and the
 * client points the user through them.
 *
 * Each step's `target` is a `data-companion-label` value on a real control. The
 * overlay points at step N's target and advances to N+1 when the user clicks it
 * (see companion-walkthrough.tsx). This is a plain .ts file (not .tsx) because
 * it exports hooks/helpers alongside the store (Vite Fast Refresh rule).
 */

import { create } from 'zustand'

/**
 * Marker attribute on every panel's settings gear. Kept SEPARATE from the gear's
 * `data-companion-label` (which stays panel-identifying, e.g. `scanner-settings`,
 * so the Companion's pointing lands on the right panel): the walkthrough is
 * already anchored to a specific panel, so it locates the gear by this
 * panel-agnostic marker scoped to that panel — it must not rely on a shared
 * label. See panel-actions.tsx.
 */
export const GEAR_SELECTOR = '[data-panel-settings]'

/**
 * Authored step targets that map to a marker/selector rather than a literal
 * `data-companion-label`. `panel-settings-gear` is a SEMANTIC target — "this
 * panel's settings gear" — resolved via GEAR_SELECTOR.
 */
const TARGET_SELECTORS: Record<string, string> = {
  'panel-settings-gear': GEAR_SELECTOR,
}

/** CSS selector for an authored step target. */
export function targetSelector(target: string): string {
  return TARGET_SELECTORS[target] ?? `[data-companion-label="${target}"]`
}

/**
 * The nearest ancestor of a panel's body that also holds its settings gear.
 *
 * Panels render in react-mosaic: the gear lives in the window TOOLBAR
 * (`.mosaic-window-toolbar`), a SIBLING of the `[data-panel-id]` body — so it is
 * never a descendant of the body, and we can't scope targets by the body alone.
 * Walking up from the body to the first ancestor that contains the settings gear
 * lands on that panel's `.mosaic-window`, which wraps both the toolbar and the
 * body. Layout-agnostic (works for any wrapper, not just mosaic) because it keys
 * off the gear marker, not a class name.
 */
export function panelWindowFromBody(body: Element | null): HTMLElement | null {
  let el = body as HTMLElement | null
  while (el) {
    if (el.querySelector(GEAR_SELECTOR)) return el
    el = el.parentElement
  }
  return null
}

/** Same, resolved from a panel's `data-panel-id`. */
export function panelWindowOf(panelId: string): HTMLElement | null {
  return panelWindowFromBody(document.querySelector(`[data-panel-id="${panelId}"]`))
}

export interface WalkthroughStep {
  /** Human instruction shown in the step tooltip. */
  instruction: string
  /** The `data-companion-label` of the control this step points at. */
  target: string
}

/**
 * The panel a walkthrough is anchored to. App-wide labels (e.g.
 * `panel-settings-gear`) exist on every panel, so targets rendered INSIDE a
 * panel are resolved within this panel's `data-panel-id` subtree — pointing at
 * the Scanner's gear, not whatever gear happens to be first in the DOM. Targets
 * that live in a portaled sheet (the Colors tab, the Add button) fall back to a
 * global lookup since only one settings sheet is open at a time.
 */
export interface WalkthroughScope {
  /** `data-panel-id` of the anchored panel. */
  panelId: string
}

interface WalkthroughState {
  /** The active step list (empty when no walkthrough is running). */
  steps: WalkthroughStep[]
  /** Index of the current step within `steps`. */
  index: number
  /** Whether a walkthrough is currently running. */
  active: boolean
  /** The panel this walkthrough points within (null = unscoped / global). */
  scope: WalkthroughScope | null
  /**
   * Fired when the walkthrough finishes by advancing past its last step.
   * NOT fired on dismiss: abandoning a tour is not completing it. Set per
   * launch (Learn lessons + onboarding checklist mark completion through it).
   */
  onComplete: (() => void) | null
  /** Launch a walkthrough from step 0. No-op on an empty list. */
  start: (
    steps: WalkthroughStep[],
    scope?: WalkthroughScope | null,
    onComplete?: (() => void) | null,
  ) => void
  /** Advance to the next step; completing the last step ends the walkthrough. */
  advance: () => void
  /** End the walkthrough immediately (dismiss / Esc / completion). */
  dismiss: () => void
}

export const useWalkthroughStore = create<WalkthroughState>((set, get) => ({
  steps: [],
  index: 0,
  active: false,
  scope: null,
  onComplete: null,
  start: (steps, scope = null, onComplete = null) => {
    if (!steps || steps.length === 0) return
    set({ steps: [...steps], index: 0, active: true, scope, onComplete })
  },
  advance: () => {
    const { active, index, steps, onComplete } = get()
    if (!active) return
    if (index + 1 >= steps.length) {
      // Past the last step: walkthrough complete. Reset BEFORE notifying so
      // a listener that launches another walkthrough sees a clean store.
      set({ steps: [], index: 0, active: false, scope: null, onComplete: null })
      onComplete?.()
    } else {
      set({ index: index + 1 })
    }
  },
  dismiss: () => set({ steps: [], index: 0, active: false, scope: null, onComplete: null }),
}))

/**
 * Imperative launcher for non-React callers (the tool-executor runs outside the
 * React tree). Guards against empty/malformed step lists.
 */
export function startWalkthrough(
  steps: WalkthroughStep[],
  scope?: WalkthroughScope | null,
  onComplete?: (() => void) | null,
): void {
  useWalkthroughStore.getState().start(steps, scope, onComplete)
}
